import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { PrimaryButton } from './PrimaryButton';
import { FloatingLabelInput } from './FloatingLabelInput';
import { CmsModal } from './CmsModal';
import { signup } from '../../lib/professional-api';
import { setAuth } from '../../lib/auth';
import { apiErrorMessage } from '../../lib/api';
import { toast } from '../../lib/toast';
import {
  sanitizeName,
  passwordStrengthError,
  STRONG_PASSWORD_MESSAGE,
} from '../../lib/validators';

type CmsKind =
  | { code: 'TERMS_CONDITIONS'; title: 'Terms of Use' }
  | { code: 'PRIVACY_POLICY'; title: 'Privacy Policy' };

export function ProSignUpStep2Form() {
  const navigate = useNavigate();
  const location = useLocation();
  // Email comes from step 1's `navigate(..., { state: { email } })`.
  // Direct landings (refresh / typed URL / bookmark) bounce back to step 1
  // — keeps the wizard linear and prevents browser autofill from
  // hijacking the email field after a different account was used.
  const presetEmail = (location.state as { email?: string } | null)?.email ?? '';
  if (!presetEmail) {
    return <Navigate to="/professional/signup" replace />;
  }

  const [formData, setFormData] = useState({
    email: presetEmail,
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({
    email: false,
    firstName: false,
    lastName: false,
    password: false,
    confirmPassword: false,
  });

  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeCms, setActiveCms] = useState<CmsKind | null>(null);

  const passwordValid = (pw: string) => passwordStrengthError(pw) === null;
  const emailValid = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const handleSubmit = async () => {
    const newErrors = {
      email: !emailValid(formData.email),
      firstName: !formData.firstName.trim(),
      lastName: !formData.lastName.trim(),
      password: !passwordValid(formData.password),
      confirmPassword:
        !formData.confirmPassword ||
        formData.password !== formData.confirmPassword,
    };
    setErrors(newErrors);
    const termsMissing = !agreeToTerms;
    setTermsError(termsMissing ? 'You must accept the terms to create an account.' : null);
    if (Object.values(newErrors).some(Boolean) || termsMissing) return;

    setSubmitting(true);
    try {
      const res = await signup({
        email: formData.email.trim().toLowerCase(),
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        password: formData.password,
        accept_terms: true,
      });
      // Persist token + user, then send the pro to the "we just emailed
      // you" success page where they can resend or check their inbox.
      setAuth(res.accessToken, res.user);
      toast.success('Account created — check your email to verify.');
      navigate('/professional/signup/success', {
        state: { email: res.user.email },
      });
    } catch (err) {
      // Toast-only per project convention — inline duplication was
      // confusing customers (same message shown twice) and could
      // surface raw DB error text that the toast had already cleaned
      // up. Field-level validation errors stay inline; auth/API
      // failures go to the toast layer only.
      toast.error(apiErrorMessage(err, 'Could not create account.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full flex flex-col items-center max-w-[420px] mx-auto">
      <div className="text-center mb-12">
        <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] !text-[25px] md:!text-[34px] leading-[32px] md:leading-[48px] mb-2">
          Tell us more about you
        </h1>
        <p className="font-['DM_Sans',sans-serif] font-normal leading-[21px] text-[#908e8e] text-[14px] tracking-[-0.1px]">
          Enter your professional details to proceed further
        </p>
      </div>

      <div className="w-full flex flex-col gap-6">
        <FloatingLabelInput
          label="Email address"
          type="email"
          value={formData.email}
          onChange={(val) => {
            setFormData({ ...formData, email: val });
            setErrors({ ...errors, email: false });
          }}
          icon={``}
          disabled={!!presetEmail}
          className={`w-full ${presetEmail ? 'opacity-70' : ''}`}
          error={errors.email}
          errorMessage="Please enter a valid email address"
        />

        <div className="grid grid-cols-2 gap-4">
          <FloatingLabelInput
            label="First Name *"
            type="text"
            value={formData.firstName}
            onChange={(val) => {
              setFormData({ ...formData, firstName: sanitizeName(val) });
              setErrors({ ...errors, firstName: false });
            }}
            icon={``}
            className="w-full"
            error={errors.firstName}
            errorMessage="First name is required"
          />
          <FloatingLabelInput
            label="Last Name *"
            type="text"
            value={formData.lastName}
            onChange={(val) => {
              setFormData({ ...formData, lastName: sanitizeName(val) });
              setErrors({ ...errors, lastName: false });
            }}
            icon={``}
            className="w-full"
            error={errors.lastName}
            errorMessage="Last name is required"
          />
        </div>

        <FloatingLabelInput
          label="Create Password"
          type="password"
          value={formData.password}
          onChange={(val) => {
            setFormData({ ...formData, password: val });
            setErrors({ ...errors, password: false });
          }}
          icon={``}
          className="w-full"
          error={errors.password}
          errorMessage={STRONG_PASSWORD_MESSAGE}
        />

        <FloatingLabelInput
          label="Confirm Password"
          type="password"
          value={formData.confirmPassword}
          onChange={(val) => {
            setFormData({ ...formData, confirmPassword: val });
            setErrors({ ...errors, confirmPassword: false });
          }}
          icon={``}
          className="w-full"
          error={errors.confirmPassword}
          errorMessage={
            !formData.confirmPassword
              ? 'Please confirm your password'
              : 'Passwords do not match'
          }
        />

        <div className="flex flex-col gap-1">
          <div className="flex items-start gap-[10px]">
            <button
              type="button"
              aria-label={agreeToTerms ? 'Untick terms agreement' : 'Tick terms agreement'}
              onClick={() => {
                setAgreeToTerms(!agreeToTerms);
                setTermsError(null);
              }}
              className={`mt-1 size-[20px] rounded-full flex items-center justify-center transition-colors border shrink-0 cursor-pointer ${agreeToTerms ? 'bg-[#8e2a2f] border-[#8e2a2f]' : `bg-white ${termsError ? 'border-red-500' : 'border-[#eee]'}`}`}
            >
              {agreeToTerms && <span className="text-white text-[12px]">✓</span>}
            </button>
            <p className="font-['DM_Sans',sans-serif] font-normal leading-[21px] text-[#908e8e] text-[14px] tracking-[-0.1px]">
              By clicking Create account, I agree that I have read and accepted the{' '}
              <button
                type="button"
                onClick={() => setActiveCms({ code: 'TERMS_CONDITIONS', title: 'Terms of Use' })}
                className="text-[#8e2a2f] font-bold underline hover:no-underline"
              >
                Terms of Use
              </button>
              {' '}and{' '}
              <button
                type="button"
                onClick={() => setActiveCms({ code: 'PRIVACY_POLICY', title: 'Privacy Policy' })}
                className="text-[#8e2a2f] font-bold underline hover:no-underline"
              >
                Privacy Policy
              </button>
              .
            </p>
          </div>
          {termsError && (
            <p className="font-['DM_Sans',sans-serif] text-[12px] text-red-500 pl-[30px]">
              {termsError}
            </p>
          )}
        </div>

        <PrimaryButton
          onClick={handleSubmit}
          className="w-full py-4 mt-2"
          disabled={submitting}
        >
          {submitting ? 'Creating account…' : 'Create Account'}
        </PrimaryButton>
      </div>

      <CmsModal
        isOpen={!!activeCms}
        onClose={() => setActiveCms(null)}
        code={activeCms?.code ?? 'TERMS_CONDITIONS'}
        userType="PROFESSIONAL"
        fallbackTitle={activeCms?.title}
      />
    </div>
  );
}
